import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BinocularsSearchSeeingIdentify,
  LockClosedSecure,
  PostPinNoteNotificationAlert
} from '@brightlocal/illustrations';
import { SUPPORT_EMAIL_HREF } from '@brightlocal/shared';
import { SplitLayout, SplitLayoutContentLeft, SplitLayoutContentRight } from '@brightlocal/ui-components/split-layout';

import { useAppNavigation } from 'shared/hooks';

import { LeftSide } from './LeftSide';
import { ERROR_PAGE_TYPES } from '../consts';
import { useErrorPageTracking } from '../hooks';
import type { ErrorPageType } from '../types';

interface Props {
  /** The type of the error page */
  type: ErrorPageType;
  /** The title of the error page */
  title: string;
  /** The content of the error page */
  content: string;
}

const getRightSideImage = (type: ErrorPageType) => {
  switch (type) {
    case ERROR_PAGE_TYPES.FORBIDDEN_PAGE:
      return <LockClosedSecure size={280} />;
    case ERROR_PAGE_TYPES.NOT_FOUND_PAGE:
      return <BinocularsSearchSeeingIdentify size={280} />;
    case ERROR_PAGE_TYPES.INTERNAL_SERVER_ERROR_PAGE:
      return <PostPinNoteNotificationAlert size={280} />;
    default:
      return null;
  }
};

export const ErrorPage = ({ type, title, content }: Props) => {
  const { t } = useTranslation();
  // `/` lands a signed-in user on All Locations and an anonymous visitor on sign-up.
  const { toHome } = useAppNavigation();
  const { trackErrorPageSeen } = useErrorPageTracking();

  useEffect(() => {
    trackErrorPageSeen(type);
  }, [type, trackErrorPageSeen]);

  const handleContactSupport = () => {
    window.location.href = SUPPORT_EMAIL_HREF;
  };

  return (
    <SplitLayout dataHook="error-page-layout">
      <SplitLayoutContentLeft>
        <LeftSide
          content={content}
          description={t('errors.errorWithCode', { code: type })}
          title={title}
          onContactSupport={handleContactSupport}
          onGoHome={toHome}
        />
      </SplitLayoutContentLeft>
      <SplitLayoutContentRight>
        <div aria-hidden="true" className="flex h-full w-full items-center justify-center">
          {getRightSideImage(type)}
        </div>
      </SplitLayoutContentRight>
    </SplitLayout>
  );
};
